import { KeyboardEvent, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { placeFromPoint, PopupBackdrop, popupZIndex, portalEventGuards, useDismiss, viewportMargin } from './popup.tsx';
import type { ClearLabel } from './repeating_row_cells.ts';

// The planner's right-click (long-press on a phone) menu for one cell (issue #4169): Choose verses…, Copy, Paste
// and Clear, each listed only when the cell can do it. Clear is named from what pressing it does (issue #4211).

export interface CellContextMenuProps {
  // the pointer, in viewport coordinates: the menu opens below and to the right of it, or above or to the left where it
  // would not fit, as a native menu does, and is moved inside the viewport when it fits on neither side
  x: number;
  y: number;
  onChooseVerses?: () => void;
  onCopy?: () => void;
  onPaste?: () => void;
  // the Clear item, with its name (getClearLabel): "Clear cell", or in a church's repeating row "Stop repeat here" when
  // it writes a blank and "Resume repeat" when it removes one
  clear?: { label: ClearLabel; onSelect: () => void };
  onClose: () => void;
  // for Storybook only: render in place, in the page flow, instead of floating over the page at x, y
  isInline?: boolean;
}

export const CellContextMenu = ({
  x,
  y,
  onChooseVerses,
  onCopy,
  onPaste,
  clear,
  onClose,
  isInline,
}: CellContextMenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!menu || isInline) {
      return;
    }
    menu.style.left = `${placeFromPoint(x, menu.offsetWidth, window.innerWidth, viewportMargin)}px`;
    menu.style.top = `${placeFromPoint(y, menu.offsetHeight, window.innerHeight, viewportMargin)}px`;
    // the first item has the focus, so Enter chooses it at once; opened by a pointer, it shows no focus ring
    (getItems(menu)[0] ?? menu).focus();
  }, [isInline, x, y]);

  useDismiss(onClose, { isClosedByScroll: true });

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Ctrl+C, Ctrl+V and Ctrl+X (Cmd on a Mac) close the menu and go on to the planner, which copies or pastes the
    // cell the menu was opened on; Ctrl or Cmd pressed on its own, on the way to one of them, leaves the menu open
    if ((event.ctrlKey || event.metaKey) && clipboardKeys.has(event.key.toLowerCase())) {
      onClose();
      return;
    }
    event.stopPropagation();
    if (event.key === 'Escape') {
      onClose();
      return;
    }
    // Tab and Shift+Tab close the menu as Escape does, as the ARIA menu pattern has it, and the focus goes back to the
    // cell rather than on to whatever comes next in the page
    if (event.key === 'Tab') {
      event.preventDefault();
      onClose();
      return;
    }
    const isForward = event.key === 'ArrowDown';
    if (!isForward && event.key !== 'ArrowUp') {
      return;
    }
    event.preventDefault();
    const items = menuRef.current ? getItems(menuRef.current) : [];
    const index = items.findIndex((item) => item === document.activeElement);
    const step = isForward ? 1 : -1;
    const next = index === -1 ? (step === 1 ? 0 : items.length - 1) : (index + step + items.length) % items.length;
    items[next]?.focus();
  };

  const select = (action: () => void) => () => {
    onClose();
    action();
  };

  const menu = (
    <MenuBox
      ref={menuRef}
      role="menu"
      tabIndex={-1}
      $isInline={isInline}
      style={isInline ? undefined : { left: x, top: y }}
      {...portalEventGuards}
      onKeyDown={onKeyDown}
    >
      {onChooseVerses && <MenuItem onClick={select(onChooseVerses)}>Choose verses…</MenuItem>}
      {onCopy && <MenuItem onClick={select(onCopy)}>Copy</MenuItem>}
      {onPaste && <MenuItem onClick={select(onPaste)}>Paste</MenuItem>}
      {clear && (
        <>
          {(onChooseVerses || onCopy || onPaste) && <MenuSeparator role="separator" />}
          <MenuItem isDangerous={true} onClick={select(clear.onSelect)}>
            {clear.label}
          </MenuItem>
        </>
      )}
    </MenuBox>
  );
  if (isInline) {
    return menu;
  }
  return createPortal(
    <>
      <PopupBackdrop onDismiss={onClose} isPassingThrough={true} />
      {menu}
    </>,
    document.body,
  );
};

const clipboardKeys = new Set(['c', 'v', 'x']);

function getItems(menu: HTMLElement) {
  return [...menu.querySelectorAll<HTMLButtonElement>('[role="menuitem"]')];
}

const MenuItem = ({
  isDangerous,
  onClick,
  children,
}: {
  isDangerous?: boolean;
  onClick: () => void;
  children: string;
}) => (
  <MenuButton type="button" role="menuitem" tabIndex={-1} $isDangerous={isDangerous} onClick={onClick}>
    {children}
  </MenuButton>
);

const MenuBox = styled.div<{ $isInline?: boolean }>`
  position: ${(props) => (props.$isInline ? 'static' : 'fixed')};
  z-index: ${popupZIndex};
  box-sizing: border-box;
  width: 196px;
  padding: 6px 0;
  display: flex;
  flex-direction: column;
  background: var(--color-background);
  color: var(--color-text);
  border: 1px solid var(--color-button-border);
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  outline: none;
`;

const MenuButton = styled.button<{ $isDangerous?: boolean }>`
  box-sizing: border-box;
  width: 100%;
  height: 34px;
  padding: 0 14px;
  display: flex;
  align-items: center;
  border: 0;
  background: transparent;
  color: ${(props) => (props.$isDangerous ? 'var(--color-dangerous-button)' : 'var(--color-text)')};
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 15px;
  text-align: left;
  cursor: pointer;
  outline: none;

  &:hover,
  &:focus-visible,
  &:active {
    background: var(--color-background-selected-item);
  }
`;

const MenuSeparator = styled.div`
  height: 1px;
  margin: 6px 0;
  background: var(--color-button-border);
`;
